import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import PastWorkCard from "../components/PastWorkCard";
import "./css/PastWorks.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function PastWorks() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pastWorks, setPastWorks] = useState([]);
  const [loadingWorks, setLoadingWorks] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workToDelete, setWorkToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      pastWorks: "Past Works",
      warningMessage: "Please add at least one past work to mark this section as completed!",
      addWork: "Add Work",
      noWorkAdded: "No work added",
      loading: "Loading",
      deleteConfirm: "Are you sure you want to delete this past work?",
      cancel: "Cancel",
      confirm: "Confirm",
      deleting: "Deleting...",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      pastWorks: "الأعمال السابقة",
      warningMessage: "يرجى إضافة عمل سابق واحد على الأقل لتمييز هذا القسم كمكتمل!",
      addWork: "إضافة عمل",
      noWorkAdded: "لم يتم إضافة أي عمل",
      loading: "جاري التحميل",
      deleteConfirm: "هل أنت متأكد أنك تريد حذف هذا العمل السابق؟",
      cancel: "إلغاء",
      confirm: "تأكيد",
      deleting: "جاري الحذف...",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch past works
  useEffect(() => {
    const fetchPastWorks = async () => {
      try {
        setLoadingWorks(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/past-works/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          setPastWorks(data.response || []);
        }
      } catch (err) {
        console.error("Error fetching past works:", err);
        showToast(err.message || "Failed to load past works", "error");
      } finally {
        setLoadingWorks(false);
      }
    };

    fetchPastWorks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle delete click - show confirmation modal
  const handleDeleteClick = (workId) => {
    setWorkToDelete(workId);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!workToDelete) return;

    setDeleteLoading(true);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/past-works/delete`,
        "POST",
        JSON.stringify({ id: workToDelete }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        showToast(
          lang === "ar" ? "تم الحذف بنجاح" : "Deleted successfully",
          "success"
        );
        setShowDeleteModal(false);
        setWorkToDelete(null);
        // Refresh page
        window.location.reload();
      } else {
        showToast(
          data?.message || (lang === "ar" ? "فشل الحذف" : "Failed to delete"),
          "error"
        );
        setDeleteLoading(false);
      }
    } catch (err) {
      console.error("Error deleting past work:", err);
      showToast(
        err.message || (lang === "ar" ? "فشل الحذف" : "Failed to delete"),
        "error"
      );
      setDeleteLoading(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setWorkToDelete(null);
  };

  // Loading screen
  if (loading) {
    return (
      <div className="past-works-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="past-works-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="past-works-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="past-works-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="past-works-content">
        {/* Section Heading */}
        <h2 className="past-works-heading">{t.pastWorks}</h2>

        {/* Warning Banner */}
        {profile && !profile.past_works_completed && (
          <div className="past-works-warning-banner">
            <p>{t.warningMessage}</p>
          </div>
        )}

        {/* Add Work Button */}
        <button 
          className="past-works-add-btn"
          onClick={() => navigate("/add-past-work")}
          disabled={pastWorks.length >= 10}
        >
          {t.addWork}
        </button>

        {/* Past Works List */}
        {loadingWorks ? (
          <div className="past-works-loading-works">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : pastWorks.length === 0 ? (
          <p className="past-works-empty">{t.noWorkAdded}</p>
        ) : (
          <div className="past-works-list">
            {pastWorks.map((work) => (
              <PastWorkCard
                key={work._id}
                work={work}
                onDelete={handleDeleteClick}
                onViewDetails={(workId) => navigate(`/edit-past-work/${workId}`)}
                lang={lang}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="delete-modal-overlay"
          onClick={handleDeleteCancel}
        >
          <div
            className="delete-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="delete-modal-title">{t.deleteConfirm}</h3>
            <div className="delete-modal-buttons">
              <button
                className="delete-modal-btn cancel-btn"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
              >
                {t.cancel}
              </button>
              <button
                className="delete-modal-btn confirm-btn"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span className="delete-spinner"></span>
                    {t.deleting}
                  </>
                ) : (
                  t.confirm
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

